import React, { useState, useEffect, useRef } from 'react';
import { runPrefetch, requestPersistOnce } from '../services/prefetch';

// Module-level store so download state persists across component mounts.
// This prevents the mobile-menu chip from aborting a running download when
// the user closes the menu (which unmounts that chip instance).
const _store = {
  status: 'idle',  // 'idle' | 'running' | 'done' | 'error'
  progress: { phase: 'daily', fetched: 0, total: 0, errors: 0 },
  denialThisRun: false,
  abortController: null,
  listeners: new Set(),
};

function _snap() {
  return {
    status: _store.status,
    progress: { ..._store.progress },
    denialThisRun: _store.denialThisRun,
  };
}

function _notify() {
  const s = _snap();
  _store.listeners.forEach(fn => fn(s));
}

// Exported for unit tests only.
export const __resetStore = () => {
  _store.abortController?.abort();
  _store.status = 'idle';
  _store.progress = { phase: 'daily', fetched: 0, total: 0, errors: 0 };
  _store.denialThisRun = false;
  _store.abortController = null;
  _store.listeners.clear();
};

// Download (tray-arrow) glyph, shared by the idle + error states / both variants.
const DOWNLOAD_ICON_D = 'M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4';

function runningLabel(p) {
  const errs = p.errors > 0 ? ` · ${p.errors} failed` : '';
  switch (p.phase) {
    case 'daily':      return `Loading daily…${errs}`;
    case 'digests':    return `Loading digests…${errs}`;
    case 'articles':   return `Downloading… ${p.fetched}/${p.total} articles${errs}`;
    case 'highlights': return `Loading highlights…${errs}`;
    default:           return `Downloading…${errs}`;
  }
}

// Compact progress shown below the `lg` breakpoint, where the full running label
// is hidden. Reason: hiding all text left the header chip as a bare spinner on
// mid-width/foldable screens, making an in-progress download look "stuck" (§9a).
function compactRunningLabel(p) {
  return p.total > 0 ? `${p.fetched}/${p.total}` : 'Loading…';
}

const DownloadForOfflineChip = ({ variant = 'header' }) => {
  const [storeSnap, setStoreSnap] = useState(_snap);
  const collapseTimerRef = useRef(null);

  useEffect(() => {
    _store.listeners.add(setStoreSnap);
    // Sync on mount; if download finished while chip was unmounted, start collapse.
    const current = _snap();
    setStoreSnap(current);
    if (current.status === 'done') {
      collapseTimerRef.current = setTimeout(() => {
        _store.status = 'idle';
        _notify();
      }, 4000);
    }
    return () => {
      _store.listeners.delete(setStoreSnap);
      clearTimeout(collapseTimerRef.current);
      // Reason: intentionally NOT aborting — download must survive menu close.
    };
  }, []);

  const { status, progress, denialThisRun } = storeSnap;

  async function handleClick() {
    if (status === 'running') return;
    clearTimeout(collapseTimerRef.current);

    _store.abortController?.abort();
    const ac = new AbortController();
    _store.abortController = ac;
    _store.status = 'running';
    _store.progress = { phase: 'daily', fetched: 0, total: 0, errors: 0 };
    _store.denialThisRun = false;
    _notify();

    const persist = await requestPersistOnce().catch(() => ({ denied: false, alreadyAsked: true }));
    // Guard: if a second click started a newer download, bail.
    if (ac.signal.aborted) return;
    if (persist.denied && !persist.alreadyAsked) {
      _store.denialThisRun = true;
      _notify();
    }

    let result = null;
    try {
      result = await runPrefetch({
        signal: ac.signal,
        onProgress: (p) => {
          _store.progress = { ...p };
          _notify();
        },
      });
    } catch {
      // unexpected throw from runPrefetch
    } finally {
      _store.abortController = null;
    }

    if (ac.signal.aborted) return;

    if (!result || result.errors > 0) {
      _store.status = 'error';
      _notify();
    } else {
      _store.status = 'done';
      _notify();
      collapseTimerRef.current = setTimeout(() => {
        _store.status = 'idle';
        _notify();
      }, 4000);
    }
  }

  const isMenu = variant === 'menu';
  // Header chip mirrors the nav: icon-only between sm and lg, icon + label at
  // lg+ (whole chip stays compact in every state so clicking never balloons it
  // into a long badge). The menu variant is a full-width dropdown row that
  // always shows its label.
  const menuRow = 'flex items-center w-full px-4 py-2.5 text-sm text-body-text hover:bg-clay-bg transition-colors';
  const headerIcon = 'w-5 h-5 lg:mr-1.5';
  const headerLabel = 'hidden lg:inline';

  if (status === 'running') {
    return isMenu ? (
      <span className={menuRow + ' cursor-default'}>
        <span className="spinner-fun-sm mr-2" aria-hidden="true" />
        {runningLabel(progress)}
      </span>
    ) : (
      <span
        className="pill-clay pill-clay-active inline-flex items-center text-sm whitespace-nowrap cursor-default"
        title={runningLabel(progress)}
      >
        <span className="spinner-fun-sm lg:mr-1.5" aria-hidden="true" />
        <span className="ml-1 lg:hidden">{compactRunningLabel(progress)}</span>
        <span className={headerLabel}>{runningLabel(progress)}</span>
      </span>
    );
  }

  if (status === 'done') {
    const note = denialThisRun && (
      <span className="ml-2 text-xs text-secondary-text">(storage not persistent)</span>
    );
    return isMenu ? (
      <span className={menuRow + ' cursor-default'}>
        Ready for offline ✓{note}
      </span>
    ) : (
      <span className="badge-fun-mint text-sm whitespace-nowrap cursor-default" title="Ready for offline">
        <svg className={headerIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span className={headerLabel}>Ready for offline ✓{note}</span>
      </span>
    );
  }

  if (status === 'error') {
    return isMenu ? (
      <button type="button" onClick={handleClick} className={menuRow} aria-label="Retry download for offline">
        <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={DOWNLOAD_ICON_D} />
        </svg>
        Download incomplete — Retry
      </button>
    ) : (
      <button
        type="button"
        onClick={handleClick}
        className="badge-fun-coral text-sm whitespace-nowrap"
        aria-label="Retry download for offline"
        title="Download incomplete — Retry"
      >
        <svg className={headerIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={DOWNLOAD_ICON_D} />
        </svg>
        <span className={headerLabel}>Download incomplete — Retry</span>
      </button>
    );
  }

  // idle
  return isMenu ? (
    <button type="button" onClick={handleClick} className={menuRow} aria-label="Download for offline">
      <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={DOWNLOAD_ICON_D} />
      </svg>
      Download for offline
    </button>
  ) : (
    <button
      type="button"
      onClick={handleClick}
      className="pill-clay inline-flex items-center text-sm whitespace-nowrap"
      aria-label="Download for offline"
      title="Download for offline"
    >
      <svg className={headerIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={DOWNLOAD_ICON_D} />
      </svg>
      <span className={headerLabel}>Download for offline</span>
    </button>
  );
};

export default DownloadForOfflineChip;
